import { useState } from "react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";

const StravaIcon = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="shrink-0">
    <path d="M15.387 17.944l-2.089-4.116h-3.065L15.387 24l5.15-10.172h-3.066m-7.008-5.599l2.836 5.598h4.172L10.463 0l-7 13.828h4.169" />
  </svg>
);

export default function StravaConnectButton({ connected, username, onChange, variant = "default" }) {
  const [busy, setBusy] = useState(false);

  const connect = async () => {
    setBusy(true);
    try {
      const origin = encodeURIComponent(window.location.origin);
      const { data } = await api.get(`/strava/auth-url?origin=${origin}`);
      window.location.href = data.url;
    } catch (err) {
      toast.error("Could not start Strava connect");
      setBusy(false);
    }
  };

  const sync = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/strava/sync");
      toast.success(`Synced! ${data.strava_activities} Strava activities on file`);
      onChange?.();
    } catch {
      toast.error("Sync failed");
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Strava? Your past synced activities will stay.")) return;
    setBusy(true);
    try {
      await api.post("/strava/disconnect");
      toast.success("Strava disconnected");
      onChange?.();
    } catch {
      toast.error("Could not disconnect");
    } finally {
      setBusy(false);
    }
  };

  if (connected) {
    return (
      <div className="flex items-center gap-2 flex-wrap" data-testid="strava-connected">
        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 hairline bg-orange-600/10 border-orange-600/40 text-orange-500 font-mono text-[11px] tracking-widest uppercase">
          {StravaIcon} STRAVA · {username || "CONNECTED"}
        </span>
        <Button onClick={sync} disabled={busy} data-testid="strava-sync-btn" size="sm"
          className="rounded-none bg-orange-600 hover:bg-orange-500 font-display tracking-wider text-xs">
          {busy ? "SYNCING…" : "SYNC NOW"}
        </Button>
        <Button onClick={disconnect} disabled={busy} data-testid="strava-disconnect-btn" size="sm" variant="outline"
          className="rounded-none border-zinc-700 bg-transparent text-zinc-400 hover:text-white hover:bg-zinc-900 font-display tracking-wider text-xs">
          DISCONNECT
        </Button>
      </div>
    );
  }

  return (
    <Button onClick={connect} disabled={busy} data-testid="strava-connect-btn"
      className={`rounded-none font-display tracking-wider ${variant === "ghost"
        ? "bg-transparent border border-orange-600 text-orange-500 hover:bg-orange-600 hover:text-white"
        : "bg-[#FC4C02] hover:bg-[#E34402] text-white"}`}>
      {StravaIcon}
      <span className="ml-2">{busy ? "OPENING STRAVA…" : "CONNECT STRAVA"}</span>
    </Button>
  );
}
